import type { Role } from "@orsane/schema/users";
import { SearchIcon, Trash2, UserPlus } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { authClient } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { useRemoveInvitation, useRemoveUser, useUpdateUserRole } from "@/mutations/user-mutations";
import { useInvitations, useMembers } from "@/queries/user-queries";
import { AlertDialogType } from "@/types/alert-dialog";
import { type AlertDialogImperativeHandle, DefaultAlertDialog } from "../DefaultAlertDialog";
import { InputWithIcon } from "../input-with-icon";
import LoadingSpinner from "../loading-spinner";
import { UserAvatar } from "../UserAvatar";
import { Button } from "../ui/button";
import InviteTeamMemberDialog from "./InviteTeamMemberDialog";

export default function UsersSettings() {
    const { data, isLoading } = useMembers();
    const { data: invitations } = useInvitations();
    const updateUserRole = useUpdateUserRole();
    const removeUser = useRemoveUser();
    const removeInvitation = useRemoveInvitation();
    const [searchTerm, setSearchTerm] = useState("");
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const session = authClient.useSession();
    const deleteAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);

    const filteredUsers = useMemo(() => {
        const allUsers = [
            ...(data || []).map((x) => ({ email: x.email, id: x.id, image: x.image, name: x.name, role: x.role, isInvitation: false })),
            ...(invitations || []).map((x) => ({
                email: x.email,
                id: x.id,
                image: null,
                name: "",
                role: x.role || "member",
                isInvitation: true,
            })),
        ];
        if (!searchTerm.trim()) {
            return allUsers;
        }

        const searchLower = searchTerm.toLowerCase();
        return allUsers?.filter((user) => {
            const name = user.name?.toLowerCase() || "";
            const email = user.email?.toLowerCase() || "";
            const role = user.role?.toLowerCase() || "";

            return name.includes(searchLower) || email.includes(searchLower) || role.includes(searchLower);
        });
    }, [data, invitations, searchTerm]);

    const handleRoleChange = async (userId: string, newRole: Role) => {
        await updateUserRole.mutateAsync({ userId, role: newRole });
        toast.success("Role updated", {
            description: `User role has been changed to ${newRole}`,
        });
    };

    const handleRemoveUser = (user: any) => async () => {
        const confirmed = await deleteAlertDialogRef.current!.show();
        if (confirmed) {
            if (user.isInvitation) {
                await removeInvitation.mutateAsync({ id: user.id });
            } else {
                await removeUser.mutateAsync({ userId: user.id });
            }
            toast.success("User removed", {
                description: `User has been removed from the team`,
            });
        }
    };

    const isCurrentUser = (user: any) => session.data?.user.id === user.id;
    const isOwner = (user: any) => user.role === "owner";

    return (
        <div>
            <div className="flex gap-2 justify-between">
                <InputWithIcon
                    icon={SearchIcon}
                    placeholder="Search users..."
                    className="flex-1"
                    label="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Button variant="outline" onClick={() => setInviteDialogOpen(true)}>
                    <UserPlus /> Invite team User
                </Button>
            </div>
            <div className="mt-4">
                {isLoading && (
                    <div className="">
                        <LoadingSpinner />
                    </div>
                )}

                <div className="rounded-lg border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>User</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredUsers?.map((user) => (
                                <TableRow key={user.id} className="h-12">
                                    <TableCell className="flex items-center h-12 gap-3">
                                        {!user.isInvitation && <UserAvatar user={user} size="sm" />}
                                        {!user.isInvitation && <div className="font-medium">{user.name || "Unknown User"}</div>}
                                        {user.isInvitation && <div className="font-medium pt-2">Invited user</div>}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        <div className="truncate">{user.email}</div>
                                    </TableCell>
                                    <TableCell className="w-32">
                                        {isOwner(user) || isCurrentUser(user) || user.isInvitation ? (
                                            <Badge
                                                variant="outline"
                                                className={cn({
                                                    "bg-red-100 text-red-800 border-red-200": user.role === "owner",
                                                    "bg-blue-100 text-blue-800 border-blue-200": user.role === "admin",
                                                    "bg-neutral-100 text-zinc-800 border-neutral-200": user.role === "member",
                                                })}
                                            >
                                                {user.role}
                                            </Badge>
                                        ) : (
                                            <Select
                                                value={user.role}
                                                onValueChange={(value: string | null) => {
                                                    if (value === "admin" || value === "member") {
                                                        handleRoleChange(user.id, value as Role);
                                                    }
                                                }}
                                            >
                                                <SelectTrigger className="w-full" size="sm">
                                                    <SelectValue>{(v: string) => (v === "admin" ? "Admin" : "Member")}</SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectGroup>
                                                        <SelectItem value="admin">Admin</SelectItem>
                                                        <SelectItem value="member">Member</SelectItem>
                                                    </SelectGroup>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    </TableCell>
                                    <TableCell className="w-12">
                                        {!isOwner(user) && !isCurrentUser(user) && (
                                            <Button variant="ghost" size="icon" onClick={handleRemoveUser(user)}>
                                                <Trash2 />
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <DefaultAlertDialog
                        ref={deleteAlertDialogRef}
                        type={AlertDialogType.Delete}
                        title="Remove user"
                        description="Are you sure you want to remove this user from the team?"
                    />
                </div>
            </div>
            <InviteTeamMemberDialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen} />
        </div>
    );
}
